import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AccountGateService } from '../api/account-gate.service';

/**
 * Once the initial `GET api/users/self` is forbidden, the account can't be
 * resolved and no other endpoint will function — so halt every Vigilant API call
 * outright. The bare self GET is always allowed through so the retry loop can
 * still recover if access is later granted. Placed first in the chain so blocked
 * requests never reach the auth/unauth/comms interceptors (no stray token fetch,
 * no 401→login redirect, no comms-error flagging).
 */
export const accountGateInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(environment.vigilantApiUrl)) return next(req);

  // Allow the self probe itself (exact match — not /self/sync, /self/devices, …).
  const selfUrl = `${environment.vigilantApiUrl}/api/users/self`;
  if (req.url === selfUrl) return next(req);

  // Allow the account-recovery endpoints through: these are exactly how a gated user
  // (authenticated, but with no realm/user) resolves their account. Without this they'd
  // be blocked by the very gate they're trying to clear.
  const recoveryUrls = [
    `${environment.vigilantApiUrl}/api/invites/accept`,
    `${environment.vigilantApiUrl}/api/onboarding`,
  ];
  if (recoveryUrls.includes(req.url)) return next(req);

  if (inject(AccountGateService).blocked()) {
    return throwError(() => new HttpErrorResponse({
      status: 403,
      statusText: 'Account unavailable',
      url: req.url,
    }));
  }
  return next(req);
};
